/**
 * Satellite Service
 * Fetches TLE data from Celestrak for satellites launched in the current month
 * and provides SGP4 propagation for real-time orbital positions
 */

import * as satellite from 'satellite.js';

// Celestrak API endpoints for recent launches
const CELESTRAK_RECENT_URL = 'https://celestrak.org/NORAD/elements/gp.php?GROUP=last-30-days&FORMAT=tle';
const CELESTRAK_BACKUP_URL = 'https://celestrak.org/NORAD/elements/gp.php?GROUP=active&FORMAT=tle';

// LocalStorage keys and cache duration
const CACHE_KEY = 'satellite_tle_cache';
const CACHE_TIMESTAMP_KEY = 'satellite_tle_cache_timestamp';
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours - TLE data doesn't change frequently

// Earth radius in km (for scaling)
const EARTH_RADIUS_KM = 6371;

/**
 * Save satellite TLE data to localStorage
 * We store the raw TLE lines since satrec objects can't be serialized
 */
function saveTLEToCache(satellites) {
    try {
        // Store only the serializable data (not satrec objects)
        const cacheData = satellites.map(sat => ({
            name: sat.name,
            line1: sat.line1,
            line2: sat.line2,
            noradId: sat.noradId,
            intlDesignator: sat.intlDesignator,
            launchYear: sat.launchYear,
            launchNumber: sat.launchNumber,
            inclination: sat.inclination,
            eccentricity: sat.eccentricity,
            meanMotion: sat.meanMotion,
            epochYear: sat.epochYear
        }));

        localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
        localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
        console.log(`Cached ${satellites.length} satellites to localStorage`);
    } catch (error) {
        console.warn('Failed to cache satellite data:', error.message);
    }
}

/**
 * Load satellite TLE data from localStorage cache
 * Reconstructs satrec objects from stored TLE lines
 */
function loadTLEFromCache() {
    try {
        const timestampStr = localStorage.getItem(CACHE_TIMESTAMP_KEY);
        const cacheData = localStorage.getItem(CACHE_KEY);

        if (!timestampStr || !cacheData) {
            console.log('No cached satellite data found');
            return null;
        }

        const timestamp = parseInt(timestampStr);
        const age = Date.now() - timestamp;

        if (age > CACHE_DURATION_MS) {
            console.log(`Cache expired (age: ${Math.round(age / 3600000)}h), will refresh`);
            return null;
        }

        const satellites = JSON.parse(cacheData);

        // Reconstruct satrec objects from TLE lines
        const reconstructed = satellites.map(sat => {
            try {
                const satrec = satellite.twoline2satrec(sat.line1, sat.line2);
                return { ...sat, satrec };
            } catch (error) {
                console.warn(`Failed to reconstruct satrec for ${sat.name}`);
                return null;
            }
        }).filter(sat => sat !== null);

        console.log(`Loaded ${reconstructed.length} satellites from cache (age: ${Math.round(age / 60000)}min)`);
        return reconstructed;
    } catch (error) {
        console.warn('Failed to load cached satellite data:', error.message);
        return null;
    }
}

/**
 * Parse TLE text into satellite records
 * TLE format: 3 lines per satellite (name, line1, line2)
 */
function parseTLEData(tleText) {
    const lines = tleText.trim().split('\n').map(line => line.trim()).filter(line => line.length > 0);
    const satellites = [];

    for (let i = 0; i < lines.length - 2; i += 3) {
        const name = lines[i];
        const line1 = lines[i + 1];
        const line2 = lines[i + 2];

        // Validate TLE lines start with correct indicators
        if (line1.startsWith('1 ') && line2.startsWith('2 ')) {
            try {
                const satrec = satellite.twoline2satrec(line1, line2);

                // Extract launch date from NORAD catalog number epoch
                // Line 1 columns 19-20 are the epoch year (last 2 digits)
                // Line 1 columns 21-32 are the epoch day of year
                const epochYear = parseInt(line1.substring(18, 20));
                const fullYear = epochYear < 57 ? 2000 + epochYear : 1900 + epochYear;

                // Extract international designator (launch year, launch number, piece)
                // Line 1 columns 10-17: International Designator
                const intlDesignator = line1.substring(9, 17).trim();
                const launchYear = intlDesignator.substring(0, 2);
                const launchYearFull = parseInt(launchYear) < 57 ? 2000 + parseInt(launchYear) : 1900 + parseInt(launchYear);
                const launchNumber = parseInt(intlDesignator.substring(2, 5));

                satellites.push({
                    name: name.trim(),
                    line1,
                    line2,
                    satrec,
                    noradId: line1.substring(2, 7).trim(),
                    intlDesignator,
                    launchYear: launchYearFull,
                    launchNumber,
                    // Extract orbital parameters for display
                    inclination: satrec.inclo * (180 / Math.PI), // Convert to degrees
                    eccentricity: satrec.ecco,
                    meanMotion: satrec.no * (1440 / (2 * Math.PI)), // Revs per day
                    epochYear: fullYear
                });
            } catch (error) {
                console.warn(`Failed to parse TLE for ${name}:`, error.message);
            }
        }
    }

    return satellites;
}

/**
 * Filter satellites launched in the current month
 */
function filterCurrentMonthLaunches(satellites) {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-indexed

    return satellites.filter(sat => {
        // Use international designator to determine launch date
        // Format: YYNNNP where YY is year, NNN is launch number, P is piece
        if (sat.launchYear === currentYear) {
            // For current year satellites, we need to estimate the month
            // Launch numbers are sequential within a year
            // As a heuristic: divide the year's launches proportionally
            // This is approximate - a more accurate method would use Space-Track API

            // Estimate: ~150 launches per year, so ~12-13 per month
            // Current month's launches would have higher launch numbers
            const estimatedMonthlyLaunches = 15;
            const estimatedLaunchMonth = Math.floor((sat.launchNumber - 1) / estimatedMonthlyLaunches);

            return estimatedLaunchMonth >= currentMonth - 1 && estimatedLaunchMonth <= currentMonth;
        }
        return false;
    });
}

/**
 * Get satellite position at a given time using SGP4 propagation
 * Returns position in Earth-centered inertial (ECI) coordinates
 * Scaled to Three.js scene units
 */
export function getSatellitePosition(satrec, date = new Date()) {
    const positionAndVelocity = satellite.propagate(satrec, date);

    if (!positionAndVelocity.position || typeof positionAndVelocity.position === 'boolean') {
        return null;
    }

    const positionEci = positionAndVelocity.position;

    // Convert from km to scene units (Earth radius = 2 in our scene)
    const scale = 2 / EARTH_RADIUS_KM;

    return {
        x: positionEci.x * scale,
        y: positionEci.z * scale, // Swap Y and Z for Three.js coordinate system
        z: -positionEci.y * scale,
        velocityKmS: positionAndVelocity.velocity ?
            Math.sqrt(
                positionAndVelocity.velocity.x ** 2 +
                positionAndVelocity.velocity.y ** 2 +
                positionAndVelocity.velocity.z ** 2
            ) : 0
    };
}

/**
 * Generate orbit path points for visualization
 * Returns array of 3D points representing the satellite's orbit
 */
export function getOrbitPath(satrec, numPoints = 100) {
    const points = [];
    const now = new Date();

    // Calculate orbital period in minutes from mean motion
    // Mean motion is in radians per minute, period = 2π / n
    const periodMinutes = (2 * Math.PI) / satrec.no;

    for (let i = 0; i <= numPoints; i++) {
        const timeOffset = (i / numPoints) * periodMinutes;
        const futureDate = new Date(now.getTime() + timeOffset * 60 * 1000);

        const position = getSatellitePosition(satrec, futureDate);
        if (position) {
            points.push([position.x, position.y, position.z]);
        }
    }

    return points;
}

/**
 * Calculate satellite altitude in km
 */
export function getSatelliteAltitude(satrec, date = new Date()) {
    const positionAndVelocity = satellite.propagate(satrec, date);

    if (!positionAndVelocity.position || typeof positionAndVelocity.position === 'boolean') {
        return null;
    }

    const positionEci = positionAndVelocity.position;
    const distanceFromCenter = Math.sqrt(
        positionEci.x ** 2 + positionEci.y ** 2 + positionEci.z ** 2
    );

    return distanceFromCenter - EARTH_RADIUS_KM;
}

/**
 * Fetch satellites launched in the current month
 * Uses Celestrak's recent launches catalog with localStorage caching
 */
export async function fetchCurrentMonthSatellites(forceRefresh = false) {
    // Check cache first (unless force refresh requested)
    if (!forceRefresh) {
        const cachedData = loadTLEFromCache();
        if (cachedData && cachedData.length > 0) {
            console.log('Using cached satellite data');
            return cachedData;
        }
    }

    try {
        console.log('Fetching satellite TLE data from Celestrak...');

        // Try to fetch recent launches (last 30 days)
        let response = await fetch(CELESTRAK_RECENT_URL);

        if (!response.ok) {
            console.warn('Primary Celestrak URL failed, trying backup...');
            response = await fetch(CELESTRAK_BACKUP_URL);
        }

        if (!response.ok) {
            throw new Error(`Failed to fetch TLE data: ${response.status}`);
        }

        const tleText = await response.text();
        const allSatellites = parseTLEData(tleText);

        console.log(`Parsed ${allSatellites.length} satellites from TLE data`);

        // Filter to current month launches
        const currentMonthSatellites = filterCurrentMonthLaunches(allSatellites);

        console.log(`Found ${currentMonthSatellites.length} satellites launched this month`);

        let result;
        // If we don't have enough from current month, include recent ones
        if (currentMonthSatellites.length < 5) {
            // Sort by NORAD ID (higher = more recent) and take the most recent
            const sortedByNorad = [...allSatellites].sort((a, b) =>
                parseInt(b.noradId) - parseInt(a.noradId)
            );

            // Return up to 30 most recent satellites
            result = sortedByNorad.slice(0, 30);
        } else {
            result = currentMonthSatellites;
        }

        // Save to cache for future use
        saveTLEToCache(result);

        return result;
    } catch (error) {
        console.error('Error fetching satellite data:', error);

        // Try to return stale cache data if available
        const staleCache = loadTLEFromCache();
        if (staleCache && staleCache.length > 0) {
            console.log('Using stale cache data due to fetch error');
            return staleCache;
        }

        // Return empty array on error - the component will show a fallback
        return [];
    }
}

/**
 * Force refresh satellite data (clears cache and fetches fresh data)
 */
export async function refreshSatelliteData() {
    try {
        localStorage.removeItem(CACHE_KEY);
        localStorage.removeItem(CACHE_TIMESTAMP_KEY);
        console.log('Cache cleared, fetching fresh data...');
        return await fetchCurrentMonthSatellites(true);
    } catch (error) {
        console.error('Error refreshing satellite data:', error);
        return [];
    }
}

/**
 * Get cache status for display
 */
export function getCacheStatus() {
    try {
        const timestampStr = localStorage.getItem(CACHE_TIMESTAMP_KEY);
        if (!timestampStr) {
            return { cached: false, age: null, valid: false };
        }

        const timestamp = parseInt(timestampStr);
        const age = Date.now() - timestamp;
        const valid = age < CACHE_DURATION_MS;

        return {
            cached: true,
            age: Math.round(age / 60000), // Age in minutes
            valid,
            lastUpdate: new Date(timestamp).toLocaleString()
        };
    } catch {
        return { cached: false, age: null, valid: false };
    }
}

/**
 * Get satellite info for display (tooltip, labels, etc.)
 */
export function getSatelliteInfo(satelliteData) {
    return {
        name: satelliteData.name,
        noradId: satelliteData.noradId,
        inclination: satelliteData.inclination.toFixed(2) + '°',
        eccentricity: satelliteData.eccentricity.toFixed(4),
        orbitsPerDay: satelliteData.meanMotion.toFixed(2),
        launchYear: satelliteData.launchYear,
        intlDesignator: satelliteData.intlDesignator
    };
}

/**
 * Categorize satellite by orbit type based on altitude
 */
export function getOrbitType(satrec) {
    const altitude = getSatelliteAltitude(satrec);

    if (altitude === null) return 'Unknown';
    if (altitude < 2000) return 'LEO'; // Low Earth Orbit
    if (altitude < 35786) return 'MEO'; // Medium Earth Orbit
    if (altitude >= 35786 && altitude <= 35800) return 'GEO'; // Geostationary
    return 'HEO'; // High Earth Orbit
}

const satelliteService = {
    fetchCurrentMonthSatellites,
    getSatellitePosition,
    getOrbitPath,
    getSatelliteAltitude,
    getSatelliteInfo,
    getOrbitType
};

export default satelliteService;
